import { useState, useEffect } from 'react';
import { FORM_MESSAGES, LABELS, UTILITY_CSS_CLASSES } from '../global/constants';
import { makeBEM, 
        timeStampToDateTimeLocal, 
        addDurationToDate, 
        calculateDurationBetweenDates, 
        fadeOutThenExecute, 
        getDefaultFormMsg, 
        fadeOutFormMsg, 
        gracefulFinally
} from '../global/utils';
import { createToDoItem, apiResultSuccess, editToDoItem, deleteToDoItem } from '../apihelper';
import FormStatus from '../atoms/FormStatus';
import FormButton from '../atoms/FormButton';
import { Icon } from '../atoms/Icon';
import { ConfirmDialog } from '../components/Dialogs';

function ToDoItemForm({ moduleName='todo-item-form', 
                        className='',
                        todoId=-1, 
                        toDoItem,
                        discardForm, 
                        deleteSuccess,
                        controller=false,
                    }) {

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
        
    const [formData, setFormData] = useState({ content: ''} );
	const [errors, setErrors] = useState({});
	const [formStatus, setFormStatus] = useState( FORM_MESSAGES.toDoItemFormInstructions );
    const [editMode, setEditMode] = useState(false);
    const [fadeOutTimer, setFadeOutTimer] = useState(false);
    const [ctrl, setCtrl] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const formWrapperClass = `form ${moduleName} ${className}`;
    const inputsWrapperClass = `${makeBEM('form', 'inputs-wrapper')} ${makeBEM(moduleName, 'inputs-wrapper')}`;    
    const contentErrorClass = errors.content ? UTILITY_CSS_CLASSES.hasErrors : '';
	const contentInputClass = `textarea ${makeBEM(moduleName, 'content-textarea')} ${contentErrorClass}`;
    const showDeleteBtn = editMode;
    const btnsDisabled = isSubmitting || isDeleting;

    useEffect(() => {
        if ( toDoItem ) {
            setFormData(toDoItem);
            setEditMode(true);
        }
    }, [toDoItem])

    useEffect(() => {
        if ( controller ) {
            setCtrl(controller)
        }
    }, [controller])

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedFormData = { ...formData, [name]: value };

        if ( editMode ) {
            const startDate = updatedFormData.start_date ? updatedFormData.start_date : false;
            const dueDate = updatedFormData.due_date ? updatedFormData.due_date : false;
            const duration = updatedFormData.duration;

            if ( name === 'start_date' && dueDate ) {
                if ( duration === 0 ) {
                    updatedFormData.duration = calculateDurationBetweenDates(startDate, dueDate);
                } else {
                    const newDueDate = addDurationToDate(startDate, duration);
                    updatedFormData.due_date = timeStampToDateTimeLocal(newDueDate);
                }
            }

            if ( name === 'due_date' && startDate ) {
                updatedFormData.duration = calculateDurationBetweenDates(startDate, dueDate);
            }
        }

        setFormData(updatedFormData);
    }

    const validateForm = () => {
		const newErrors = {};

		if ( !formData.content ) {
			newErrors.content = FORM_MESSAGES.contentRequired;
		}

		return newErrors;
	}

    const submitNew = (postData) => {
        setIsSubmitting(true);
        setFormStatus(FORM_MESSAGES.newToDoItemSubmit);

        try {
            createToDoItem(
                (result) => {

                    if ( apiResultSuccess(result) ) {
                        setFormStatus(FORM_MESSAGES.createdNewToDoItem);

                        if ( result.todo_item ) {
                            if ( ctrl ) {
                                ctrl.addNew(result.todo_item);
                            }
                            setFadeOutTimer(
                                fadeOutThenExecute(discardForm)
                            );
                        }
                    }
                },
                postData
            );         
        }   catch (error) {
            setFormStatus(FORM_MESSAGES.failedSubmit);
        }   finally {
            gracefulFinally(setIsSubmitting);
        }
    }

    const submitEdit = (postData) => {
        setIsSubmitting(true);
        setFormStatus(FORM_MESSAGES.editToDoItemSubmit);

        try {
            editToDoItem(
                (result) => {
                    if ( apiResultSuccess(result) ) {
                        setFormStatus(FORM_MESSAGES.savedToDoItem);

                        if ( result.todo_item ) {

                            if ( ctrl ) {
                                ctrl.editItem(result.todo_item);
                            }

                            fadeOutFormMsg(setFormStatus, '');
                        }
                    }
                },
                postData
            )

        } catch (error) {
            setFormStatus(FORM_MESSAGES.failedSubmit);
        } finally {
            gracefulFinally(setIsSubmitting);
        }
    }


    const handleSubmit = (e) => {
        e.preventDefault();
		const validationErrors = validateForm();

        if ( Object.keys(validationErrors).length > 0 ) {
			setErrors(validationErrors);
		} else {
			setErrors({});

            const postData = { ...formData, todo_id: todoId };

            if ( !editMode ) {
                submitNew(postData);
            } else {
                submitEdit(postData);
            }
        }
    }

    const getFormStatus = () => {
		if ( errors.content ) {
			return errors.content
		}

		if ( formStatus ) {
			return formStatus;
		}

        return getDefaultFormMsg(editMode, FORM_MESSAGES.toDoItemEdit, FORM_MESSAGES.toDoItemFormInstructions);
	}

    const resetForm = () => {
        setFormData({ content: '' });
        setErrors({});
    }

    const handleDiscard = () => {
        resetForm();
        discardForm();

        if ( fadeOutTimer ) {
            clearTimeout(fadeOutTimer);
        }
    }

    const handleDelete = () => {
        discardConfirmDialog();
        const postData = { id: formData.id };
        setIsDeleting(true);

        try {
            deleteToDoItem(
                (result) => {
                    if ( apiResultSuccess(result) ) {
                        setFormStatus(FORM_MESSAGES.deletedToDoItem);
                        if ( deleteSuccess ) {
                            setFadeOutTimer(
                                fadeOutThenExecute(() => {
                                    deleteSuccess(postData.id);
                                })
                            );
                        }
                    } else {
                        setFormStatus(result.message);
                    }

                    
                }, postData
            );
        } catch {
            setFormStatus(FORM_MESSAGES.failedSubmit);
        } finally {
            gracefulFinally(setIsDeleting);
        }
    }


    // Clears date field by setting form data property empty string
    // Sets duration to zero
    // Updates form data
    const clearDateField = (name='') => {
        if ( !name ) {
            return false;
        }

        const updatedFormData = { ... formData };

        if ( updatedFormData[name] ) {
            updatedFormData[name] = '';
        }

        updatedFormData.duration = 0;
        setFormData(updatedFormData);
    }

    const promptDeleteConfirm = () => {
        setShowConfirmDialog(true);
    }

    const discardConfirmDialog = () => {
        setShowConfirmDialog(false);
    }

    return (
        <div className={ formWrapperClass }>
            <form onSubmit={ handleSubmit }>
                <div className={ inputsWrapperClass }>
                    <FormStatus
                        errors={errors}
                        formStatus= { getFormStatus() }
                        editMode={editMode}
                        customClass={ makeBEM(moduleName, 'form-status') }
                    />
                    <textarea
                        id="content"
                        name="content"
                        type="text"
                        className={contentInputClass}
                        onChange={ handleChange }
                        autoComplete="off"
                        required
                        placeholder={ LABELS.toDoItemContent }
                        value={ formData.content }
                        autoFocus
                    />
                    <div className={ `${makeBEM('form', 'date-input-wrapper')} ${makeBEM(moduleName, 'date-input-wrapper')}` }>
                        <label
                            className={ `label ${makeBEM(moduleName, 'start-date-label')}` } 
                            htmlFor="start_date">{LABELS.startDate}
                        </label>
                        <div className='form__date-and-clear-button'>
                            <input 
                                id="start_date"
                                name="start_date"
                                type="datetime-local"
                                className={ `input ${makeBEM(moduleName, 'start-date')} ${formData.start_date ? '' : 'clear'}` }
                                onChange={ handleChange }
                                value={ formData.start_date ? formData.start_date : '' }
                            />
                            <button 
                                className='btn btn--icon btn--no-borderz btn--clear'
                                type="button"
                                disabled={ !formData.start_date }
                                onClick={ () => { clearDateField('start_date') } }
                            >
                                <Icon name="close"/>
                            </button>
                        </div>
                    </div>
                    <div className={ `${makeBEM('form', 'date-input-wrapper')} ${makeBEM(moduleName, 'date-input-wrapper')}` }>
                        <label 
                            className={ `label ${makeBEM(moduleName, 'due-date-label')}` }
                            htmlFor="due_date"
                        >{LABELS.endDate}</label>

                        <div className='form__date-and-clear-button'>
                            <input 
                                id="due_date"
                                name="due_date"
                                type="datetime-local"
                                className={ `input ${makeBEM(moduleName, 'due-date')} ${formData.due_date ? '' : 'clear'}` }
                                onChange={ handleChange }
                                value={ formData.due_date ? formData.due_date : '' }
                            />
                            <button 
                                className='btn btn--icon btn--no-borderz btn--clear'
                                type="button" 
                                disabled={ !formData.due_date }
                                onClick={ () => { clearDateField('due_date') } }
                            >
                                <Icon name="close"/>
                            </button>
                        </div>

                    </div>
                    <div className={ `${makeBEM('form', 'btns-wrapper')} ${makeBEM(moduleName, 'btns-wrapper')}` }>
                        <FormButton
                            customClass={ makeBEM(moduleName, 'submit-btn') }
                            type="submit"
                            onClick={handleSubmit}
                            disabled={btnsDisabled}
                            isSubmitting={isSubmitting}
                        />
                        <FormButton
                            customClass={ makeBEM(moduleName, 'discard-btn') }
                            onClick={handleDiscard}
                            disabled={btnsDisabled}
                            type="button"
                            icon="close"
                        />
                        {
                            showDeleteBtn &&
                            <FormButton
                                customClass={ makeBEM(moduleName, 'delete-btn') }
                                onClick={promptDeleteConfirm}
                                disabled={btnsDisabled}
                                isSubmitting={isDeleting}
                                type="button"
                                icon="delete"
                            />
                        }
                    </div>
                </div>
            </form>

            {
                showConfirmDialog &&
                <ConfirmDialog
                    show={showConfirmDialog}
                    message={LABELS.confirmDelete}
                    confirmAction={handleDelete}
                    discardAction={discardConfirmDialog}
                />
            }
        </div>
    )
}

export default ToDoItemForm;